// ============================================================
// FundingSection.tsx
// Solicitor-requested OIC funding fields. v1 holds three fields on the
// Case row:
//   - deductionMode (canonical token: '25_inc_vat' | '40_inc_vat' |
//     'other' | ''). The user-facing label ("25% inc VAT" etc.) lives only
//     in this UI so future relabels don't need a data migration.
//   - deductionCustomValue (free-text override, shown only when mode ===
//     'other'). Cleared in the same updateCase call when the dropdown
//     switches AWAY from 'other' — keeps the backend row consistent.
//   - ateProvider (After-The-Event insurance provider — plain string).
//
// Redesign (final sections pass): the shared SectionCards view/edit cards
// in the case shell (SELF_FRAMED) — Deduction · ATE insurance — with the
// glance strip. LOGIC UNCHANGED (same writes, same fieldKeys
// funding.deduction / funding.custom-deduction / funding.ate-provider for
// presence + live sync). Non-sequential: no Mark complete ('funding' is
// deliberately NOT in COMPLETABLE_SECTIONS).
// (Batch 2) A card's Save announces emitCaseChange(caseId, 'funding') once
// the case autosave has flushed — the Settlement page re-reads.
// ============================================================
import React from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import type { Case } from '../../types/case.types'
import FormField from '../shared/FormField'
import { broadcastFieldEdits } from '../../hooks/usePresence'
import { flushAutoSave } from '../../hooks/useAutoSave'
import { emitCaseChange } from '../../utils/caseChangeEvents'
import { inpCls, SELECT_BG, FGRID } from '../clientInfo/clientInfoRegistry'
import SectionCards, { type CardDef } from '../case/sections/SectionCards'
import { KIcon, Glance, GlanceDash, Kv, Empty, Muted, DL, BIG, CondLabel } from '../case/sections/sectionKit'

const DEDUCTION_LABEL: Record<string, string> = { '25_inc_vat': '25% inc VAT', '40_inc_vat': '40% inc VAT', other: 'Other' }
const FIELD_KEY: Partial<Record<keyof Case, string>> = {
  deductionMode: 'funding.deduction',
  deductionCustomValue: 'funding.custom-deduction',
  ateProvider: 'funding.ate-provider',
}

const FundingSection: React.FC = () => {
  const { currentCase, updateCase } = useCasevoStore()
  if (!currentCase) return null
  const c = currentCase

  // Dropdown change handler. The key rule: when switching AWAY from
  // 'other', wipe deductionCustomValue in the SAME updateCase call so the
  // backend row never holds a stale custom string against a non-'other'
  // mode. Switching TO 'other' leaves deductionCustomValue untouched.
  const handleDeductionModeChange = (nextMode: string): void => {
    if (nextMode === 'other') {
      updateCase({ deductionMode: nextMode })
    } else {
      updateCase({ deductionMode: nextMode, deductionCustomValue: '' })
      // Programmatic clear (no blur on that input) — sync it to co-editors.
      if (c.deductionCustomValue) broadcastFieldEdits(['funding.custom-deduction'])
    }
  }
  const deductionText = c.deductionMode === 'other'
    ? (c.deductionCustomValue.trim() || 'Other (not specified)')
    : DEDUCTION_LABEL[c.deductionMode] ?? ''

  const defs: CardDef<Case>[] = [
    {
      key: 'deduction', title: 'Deduction', sub: "From the client's damages", icon: <KIcon name="pound" size={17} />,
      fields: ['deductionMode', 'deductionCustomValue'],
      view: (ctx) => (
        <dl className={DL}>
          <Kv label="Deduction" full>{c.deductionMode ? <span className={BIG}>{DEDUCTION_LABEL[c.deductionMode] ?? c.deductionMode}</span> : <Empty onAdd={ctx.onAdd} />}</Kv>
          {c.deductionMode === 'other' && <Kv label="Custom deduction" full>{c.deductionCustomValue.trim() ? c.deductionCustomValue : <Muted>Not entered</Muted>}</Kv>}
        </dl>
      ),
      edit: (ctx) => (
        <div className={FGRID}>
          <div className="sm:col-span-2">
            <FormField variant="info" label="Deduction" fieldKey="funding.deduction" htmlFor={`${ctx.idPrefix}-mode`}>
              <select id={`${ctx.idPrefix}-mode`} value={c.deductionMode} onChange={(e) => handleDeductionModeChange(e.target.value)} className={`${inpCls(false)} ${SELECT_BG}`}>
                <option value="">Select</option>
                <option value="25_inc_vat">25% inc VAT</option>
                <option value="40_inc_vat">40% inc VAT</option>
                <option value="other">Other</option>
              </select>
            </FormField>
          </div>
          {c.deductionMode === 'other' && (
            <div className="sm:col-span-2">
              <FormField variant="info" label={<CondLabel label="Custom deduction" cond="shown when Other" />} fieldKey="funding.custom-deduction" htmlFor={`${ctx.idPrefix}-custom`}>
                <input id={`${ctx.idPrefix}-custom`} type="text" value={c.deductionCustomValue} onChange={(e) => updateCase({ deductionCustomValue: e.target.value })} className={inpCls(false)} />
              </FormField>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'ate', title: 'ATE insurance', sub: 'After-the-event cover', icon: <KIcon name="shield" size={17} />,
      fields: ['ateProvider'],
      view: (ctx) => (
        <dl className={DL}>
          <Kv label="ATE provider" full>{c.ateProvider.trim() ? <span className={BIG}>{c.ateProvider}</span> : <Empty onAdd={ctx.onAdd} />}</Kv>
        </dl>
      ),
      edit: (ctx) => (
        <FormField variant="info" label="ATE provider" fieldKey="funding.ate-provider" htmlFor={`${ctx.idPrefix}-ate`}>
          <input id={`${ctx.idPrefix}-ate`} type="text" value={c.ateProvider} onChange={(e) => updateCase({ ateProvider: e.target.value })} className={inpCls(false)} />
        </FormField>
      ),
    },
  ]

  const glance = (
    <Glance cells={[
      { icon: 'pound', label: 'Deduction', value: c.deductionMode ? deductionText : <GlanceDash />, sub: c.deductionMode ? 'From damages' : 'Not set' },
      { icon: 'shield', label: 'ATE provider', value: c.ateProvider.trim() || <GlanceDash />, sub: c.ateProvider.trim() ? 'After-the-event cover' : 'Not recorded' },
    ]} />
  )

  return (
    <SectionCards<Case>
      value={c}
      onChange={(changes) => updateCase(changes)}
      fieldKeyOf={(f) => FIELD_KEY[f]}
      onBroadcast={broadcastFieldEdits}
      // (Batch 2) Saved → the Settlement page (if on screen) re-reads.
      onFlush={async () => { await flushAutoSave(); emitCaseChange(c.id, 'funding') }}
      errors={[]}
      defs={defs}
      title="Funding"
      sub="How the claim is funded."
      glance={glance}
      idPrefix={`fund-${c.id}`}
    />
  )
}

export default FundingSection
